import React from 'react';

import { Dialog, Text, TextTypography as TYPOGRAPHY, Button, ButtonPriority as PRIORITY } from 'wix-ui-tpa';

import { ActionType } from '@wix/ambassador-billing-v1-subscription/types';
import { useExperiments, usePanorama, useTranslation } from '@wix/yoshi-flow-editor';
import { connect } from '../../../common/components/runtime-context';
import {
  getCancelConfirmModalSubscription,
  getDetails,
  getCancelConfirmModalSubscriptionId,
  isCancelConfirmModalOpen,
  isCancelConfirmModalSubmitting,
} from '../selectors';
import { classes } from './CancelConfirmModal.st.css';
import { RootState } from '../state';
import { Interactions } from '../../../types/interactions';
import { getCancelActionType, isRecurringSubscription } from '../domainUtils';
import { Experiments } from '../../../Experiments';
import { IActionCreators } from '../../../types/internal-types';

type RuntimeProps = ReturnType<typeof mapRuntimeToProps>;

const getDescriptionKey = (isImmediateCancel: boolean, isRecurring: boolean) => {
  if (isImmediateCancel) {
    return 'app.cancel-confirm-modal.description-immediate';
  }
  if (isRecurring) {
    return 'app.cancel-confirm-modal.description-recurring';
  }
  return 'app.cancel-confirm-modal.description';
};

export const CancelConfirmModal = ({ isOpen, subscription, details, submitting, actionCreators }: RuntimeProps) => {
  const panorama = usePanorama();
  const { t } = useTranslation();
  const { experiments } = useExperiments();
  const isRecurring = !!isRecurringSubscription(subscription);
  const isImmediateCancel =
    isRecurring &&
    getCancelActionType(
      subscription,
      details?.allowedActions,
      experiments.enabled(Experiments.SKIP_SUBSCRIPTIONS_MEMBERS_AREA),
    ) === ActionType.CANCEL;
  return (
    <Dialog
      data-hook="cancel-confirm-modal"
      contentClassName={classes.dialogContent}
      wiredToSiteColors
      isOpen={isOpen}
      onClose={() => !submitting && actionCreators.closeCancelConfirmModal()}
      closeButtonAriaLabel={t('app.cancel-confirm-modal.close')}
    >
      {isOpen ? (
        <>
          <div className={classes.title}>
            <Text
              className={classes.titleText}
              data-hook="cancel-confirm-modal-title"
              typography={TYPOGRAPHY.largeTitle}
            >
              {t('app.cancel-confirm-modal.title', {
                subscriptionName: subscription.name,
                interpolation: { escapeValue: false },
              })}
            </Text>
          </div>
          <div data-hook="cancel-confirm-modal-description" className={classes.description}>
            <Text className={classes.text} typography={TYPOGRAPHY.runningText}>
              {t(getDescriptionKey(isImmediateCancel, isRecurring))}
            </Text>
          </div>
          <div className={classes.actionContainer}>
            <Button
              className={classes.buttonPrimary}
              data-hook="CancelConfirmModal-cancel-btn"
              upgrade
              priority={PRIORITY.basicSecondary}
              disabled={submitting}
              onClick={() => actionCreators.closeCancelConfirmModal()}
            >
              {t('app.cancel-confirm-modal.cancel')}
            </Button>
            <Button
              className={classes.buttonSecondary}
              data-hook="CancelConfirmModal-confirm-btn"
              upgrade
              priority={PRIORITY.basic}
              disabled={submitting}
              onClick={() => {
                panorama.transaction(Interactions.SubscriptionCancel).start();
                actionCreators.confirmCancel();
              }}
            >
              {t('app.cancel-confirm-modal.confirm')}
            </Button>
          </div>
        </>
      ) : null}
    </Dialog>
  );
};

const mapRuntimeToProps = (state: RootState, _: {}, actionCreators: IActionCreators) => ({
  isOpen: isCancelConfirmModalOpen(state),
  subscription: getCancelConfirmModalSubscription(state),
  details: getDetails(state, getCancelConfirmModalSubscriptionId(state)!),
  submitting: isCancelConfirmModalSubmitting(state),
  actionCreators,
});

export default connect(mapRuntimeToProps)(CancelConfirmModal);
